import { useState } from "react";
import {
  Box,
  Stack,
  Typography,
  Popover,
  LinearProgress,
  CircularProgress,
  Button,
} from "@mui/material";
import RadarIcon from "@mui/icons-material/Radar";
import StopCircleOutlinedIcon from "@mui/icons-material/StopCircleOutlined";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import CancelRoundedIcon from "@mui/icons-material/CancelRounded";
import { useScan } from "../context/ScanContext";
import { useThemeColors } from "../context/ThemeContext";
import { HEADING_FONT } from "../theme/fonts";

const MONO =
  "'SFMono-Regular', 'Menlo', 'Consolas', 'Liberation Mono', monospace";

function phaseLabel(phase) {
  if (phase === "retrying") return "Retrying";
  if (phase === "saving") return "Saving";
  return "Scanning";
}

// Small stat pill used in the popover header (Up / Down / Left).
function StatPill({ label, value, color, colors }) {
  return (
    <Box
      sx={{
        flex: 1,
        textAlign: "center",
        py: 1,
        borderRadius: 2,
        background: colors.hover,
        border: `1px solid ${colors.cardBorder}`,
      }}
    >
      <Typography sx={{ fontSize: 18, fontWeight: 800, color, lineHeight: 1.1 }}>
        {value}
      </Typography>
      <Typography
        sx={{
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: "0.6px",
          textTransform: "uppercase",
          color: colors.textMuted,
          mt: 0.25,
        }}
      >
        {label}
      </Typography>
    </Box>
  );
}

// Always-visible, app-wide scan indicator for the header. Renders nothing when
// idle; while a scan runs it shows a live progress chip that opens a polished
// popover with a progress ring, tallies, and a terminal-style activity feed.
function ScanStatusIndicator() {
  const { running, cancelling, progress, cancelScan } = useScan();
  const colors = useThemeColors();
  const [anchorEl, setAnchorEl] = useState(null);

  if (!running) return null;

  const total = progress?.total || 0;
  const checked = progress?.checked || 0;
  const up = progress?.up || 0;
  const down = progress?.down || 0;
  const phase = progress?.phase || "scanning";
  const recent = progress?.recent || [];
  const pct = total ? Math.min(100, Math.round((checked / total) * 100)) : 0;
  const remaining = Math.max(0, total - checked);
  const current = recent[0];
  const label = phaseLabel(phase);

  return (
    <>
      {/* Header chip */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="center"
        spacing={1}
        onClick={(e) => setAnchorEl(e.currentTarget)}
        sx={{
          cursor: "pointer",
          pl: 1,
          pr: 1.5,
          py: "7px",
          minHeight: 36,
          boxSizing: "border-box",
          borderRadius: 999,
          border: "1px solid rgba(129, 140, 248, 0.45)",
          background:
            "linear-gradient(135deg, rgba(79,70,229,0.14), rgba(124,58,237,0.14))",
          transition: "all 0.15s ease",
          "&:hover": {
            borderColor: "#818cf8",
            boxShadow: "0 4px 14px rgba(79,70,229,0.3)",
          },
          maxWidth: { xs: 170, sm: 320 },
        }}
      >
        <Box sx={{ position: "relative", display: "flex" }}>
          <CircularProgress
            variant="determinate"
            value={100}
            size={22}
            thickness={5}
            sx={{ color: "rgba(129,140,248,0.25)" }}
          />
          <CircularProgress
            variant="determinate"
            value={pct}
            size={22}
            thickness={5}
            sx={{
              color: "#818cf8",
              position: "absolute",
              left: 0,
              "& .MuiCircularProgress-circle": { strokeLinecap: "round" },
            }}
          />
        </Box>
        <Typography
          sx={{
            flex: 1,
            textAlign: "center",
            fontSize: 12.5,
            fontWeight: 800,
            color: "#818cf8",
            whiteSpace: "nowrap",
            lineHeight: 1,
          }}
        >
          {label} {checked}/{total || "…"}
        </Typography>
        {current && (
          <Typography
            sx={{
              fontSize: 11.5,
              color: colors.textMuted,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              display: { xs: "none", sm: "block" },
              fontFamily: MONO,
              lineHeight: 1,
            }}
          >
            {current.name}
          </Typography>
        )}
      </Stack>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{
          paper: {
            sx: {
              mt: 1.25,
              width: 480,
              maxWidth: "94vw",
              borderRadius: 3.5,
              background: colors.darkMode ? "#0f172a" : "#ffffff",
              border: `1px solid ${colors.cardBorder}`,
              boxShadow: colors.darkMode
                ? "0 24px 60px rgba(0,0,0,0.65)"
                : "0 24px 60px rgba(0,0,0,0.16)",
              overflow: "hidden",
            },
          },
        }}
      >
        {/* Gradient header */}
        <Box
          sx={{
            px: 2.5,
            py: 2,
            background:
              "linear-gradient(135deg, rgba(79,70,229,0.16), rgba(124,58,237,0.10))",
            borderBottom: `1px solid ${colors.cardBorder}`,
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ mb: 1.75 }}
          >
            <Stack direction="row" alignItems="center" spacing={1.25} sx={{ minWidth: 0 }}>
              <Box
                sx={{
                  flexShrink: 0,
                  width: 38,
                  height: 38,
                  borderRadius: 2.5,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#fff",
                  background:
                    "linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)",
                  boxShadow: "0 6px 16px rgba(79,70,229,0.4)",
                  animation: "scanpulse 1.8s ease-in-out infinite",
                  "@keyframes scanpulse": {
                    "0%, 100%": { boxShadow: "0 6px 16px rgba(79,70,229,0.4)" },
                    "50%": { boxShadow: "0 6px 26px rgba(124,58,237,0.7)" },
                  },
                }}
              >
                <RadarIcon sx={{ fontSize: 20 }} />
              </Box>
              <Box>
                <Typography
                  sx={{ fontFamily: HEADING_FONT, fontSize: 14.5, fontWeight: 800, color: colors.text }}
                >
                  {label} domains
                </Typography>
                <Typography sx={{ fontSize: 11.5, color: colors.textMuted }}>
                  {remaining} remaining · live updates
                </Typography>
              </Box>
            </Stack>

            <Button
              onClick={cancelScan}
              disabled={cancelling}
              startIcon={
                cancelling ? (
                  <CircularProgress size={14} sx={{ color: "#fff" }} />
                ) : (
                  <StopCircleOutlinedIcon />
                )
              }
              sx={{
                flexShrink: 0,
                ml: "auto",
                textTransform: "none",
                fontWeight: 700,
                fontSize: 12.5,
                py: 0.4,
                px: 1.5,
                minHeight: 0,
                borderRadius: 2,
                color: "#fff",
                background: "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
                boxShadow: "0 4px 12px rgba(239, 68, 68, 0.35)",
                "& .MuiButton-startIcon > *:nth-of-type(1)": { fontSize: 16 },
                "&:hover": { boxShadow: "0 6px 16px rgba(239, 68, 68, 0.5)" },
                "&.Mui-disabled": {
                  color: "#fff",
                  background: "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
                  opacity: 0.85,
                },
              }}
            >
              {cancelling ? "Cancelling…" : "Cancel"}
            </Button>
          </Stack>

          {/* Progress */}
          <Box sx={{ position: "relative", mb: 0.75, minHeight: 20 }}>
            <Typography
              sx={{
                fontSize: 12.5,
                color: colors.textMuted,
                textAlign: "left",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                pr: 6,
              }}
            >
              <b style={{ color: colors.text }}>
                {checked}/{total}
              </b>
              {current ? ` · ${current.name}` : ""}
            </Typography>
            <Typography
              sx={{
                position: "absolute",
                right: 0,
                top: 0,
                fontSize: 15,
                fontWeight: 800,
                color: "#818cf8",
                lineHeight: 1.3,
              }}
            >
              {pct}%
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={pct}
            sx={{
              height: 8,
              borderRadius: 4,
              background: colors.hover,
              "& .MuiLinearProgress-bar": {
                borderRadius: 4,
                background: "linear-gradient(90deg, #4f46e5, #7c3aed)",
              },
            }}
          />

          {/* Tallies */}
          <Stack direction="row" spacing={1} sx={{ mt: 1.75 }}>
            <StatPill label="Up" value={up} color="#10b981" colors={colors} />
            <StatPill label="Down" value={down} color="#ef4444" colors={colors} />
            <StatPill
              label="Left"
              value={remaining}
              color={colors.text}
              colors={colors}
            />
          </Stack>
        </Box>

        {/* Terminal-style live feed */}
        <Box
          sx={{
            px: 1.5,
            pt: 1,
            pb: 0.5,
            background: "#000",
          }}
        >
          <Typography
            sx={{
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: "0.8px",
              textTransform: "uppercase",
              color: "#64748b",
              px: 0.5,
              pb: 0.5,
            }}
          >
            Live activity
          </Typography>
        </Box>
        <Box
          sx={{
            maxHeight: 300,
            overflowY: "auto",
            background: "#000",
            px: 1.5,
            pb: 1.5,
            fontFamily: MONO,
            "&::-webkit-scrollbar": { width: 8 },
            "&::-webkit-scrollbar-track": { background: "#000" },
            "&::-webkit-scrollbar-thumb": {
              background: "#1e293b",
              borderRadius: 8,
              border: "2px solid #000",
            },
            "&::-webkit-scrollbar-thumb:hover": { background: "#334155" },
            scrollbarWidth: "thin",
            scrollbarColor: "#1e293b #000",
          }}
        >
          {recent.length === 0 ? (
            <Typography
              sx={{ fontSize: 12, color: "#94a3b8", py: 2, textAlign: "center" }}
            >
              Waiting for first results…
            </Typography>
          ) : (
            recent.map((r, i) => (
              <Stack
                key={`${r.name}-${i}`}
                direction="row"
                alignItems="center"
                spacing={1}
                sx={{
                  py: 0.5,
                  px: 0.75,
                  borderRadius: 1,
                  lineHeight: 1.3,
                  transition: "background 0.15s ease",
                  "&:hover": { background: "rgba(148,163,184,0.08)" },
                  opacity: i === 0 ? 1 : 0.82,
                }}
              >
                {r.isUp ? (
                  <CheckCircleRoundedIcon
                    sx={{ fontSize: 14, color: "#10b981", flexShrink: 0 }}
                  />
                ) : (
                  <CancelRoundedIcon
                    sx={{ fontSize: 14, color: "#ef4444", flexShrink: 0 }}
                  />
                )}
                <Typography
                  sx={{
                    fontFamily: MONO,
                    fontSize: 11.5,
                    color: "#e2e8f0",
                    minWidth: 0,
                    flex: 1,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {r.name}
                  {r.retry && <span style={{ color: "#fbbf24" }}> (retry)</span>}
                </Typography>
                <Typography
                  sx={{
                    fontFamily: MONO,
                    fontSize: 11,
                    color: r.reason ? "#fca5a5" : "#64748b",
                    flexShrink: 0,
                    maxWidth: 190,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {r.reason
                    ? r.reason
                    : r.httpStatus
                    ? `HTTP ${r.httpStatus}`
                    : "no HTTP"}
                </Typography>
              </Stack>
            ))
          )}
        </Box>
      </Popover>
    </>
  );
}

export default ScanStatusIndicator;
